import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { calendarViewCopy } from "../app/i18n";
import { getIntlLocale, type AppLanguage } from "../app/language";
import { AnimatedModal, UserAvatarBadge } from "../app/ui";
import type { CalendarAssignment, CalendarAssignmentInput, CalendarAssignmentType, CalendarAssignmentTypeDefinition, Customer, Ticket, UserTeam, WorkspaceMember } from "../app/types";
import { formatDateTimeForInput, getDateTimePartsInTimeZone, getInitials, normalizeDateTimeForStorage, resolveWorkspaceMemberByEmail } from "../app/utils";
import { canManageWorkspaceAccess } from "../roles";

type CalendarViewProps = {
  currentUserEmail: string | null;
  currentUserRole: string | null;
  canCreateAssignments: boolean;
  canManageAssignmentTypes: boolean;
  isLoading?: boolean;
  language: AppLanguage;
  timezone: string;
  teamMembers: WorkspaceMember[];
  teams: UserTeam[];
  assignmentTypes: CalendarAssignmentType[];
  assignments: CalendarAssignment[];
  tickets?: Ticket[];
  customers?: Customer[];
  onOpenTicket?: (ticketId: string) => void;
  onLoadScheduledTickets?: () => Promise<void>;
  onLoadAssignmentTypeDefinitions: () => Promise<CalendarAssignmentTypeDefinition[]>;
  onCreateAssignmentType: (teamId: string, type: CalendarAssignmentType) => Promise<CalendarAssignmentTypeDefinition[]>;
  onUpdateAssignmentType: (assignmentType: CalendarAssignmentTypeDefinition, nextName: CalendarAssignmentType) => Promise<CalendarAssignmentTypeDefinition[]>;
  onDeleteAssignmentType: (assignmentTypeId: string) => Promise<CalendarAssignmentTypeDefinition[]>;
  onCreateAssignment: (input: CalendarAssignmentInput) => Promise<void>;
  onUpdateAssignment: (assignmentId: string, input: CalendarAssignmentInput) => Promise<void>;
  onDeleteAssignment: (assignmentId: string) => Promise<void>;
};

type AssignmentDraft = {
  teamId: string;
  title: string;
  type: CalendarAssignmentType;
  memberEmail: string;
  startAt: string;
  endAt: string;
  notes: string;
};

type CalendarViewMode = "week" | "month";

type CalendarSegment = {
  assignment: CalendarAssignment;
  startDayIndex: number;
  endDayIndex: number;
  span: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
};

const primaryButtonClassName = "brand-primary-button rounded-2xl px-5 py-3 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-60";
const secondaryButtonClassName = "rounded-2xl border border-[#d0d5dd] bg-white px-4 py-2.5 text-sm font-semibold text-[#475467] transition hover:bg-[#f8fafc]";
const inputClassName = "w-full rounded-2xl border border-[#d0d5dd] bg-white px-4 py-3 text-sm text-[#101828] outline-none transition focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary-ring)]";

const assignmentTypeBadgeClassNames: Record<string, string> = {
  "On-call": "bg-[#dcfce7] text-[#166534]",
  "Task rotation": "bg-[#eef2ff] text-[#4338ca]",
  Coverage: "bg-[#ecfeff] text-[#0f766e]",
};

const customAssignmentTypeBadgeClassNames = [
  "bg-[#f5f3ff] text-[#6d28d9]",
  "bg-[#eff6ff] text-[#1d4ed8]",
  "bg-[#fef3c7] text-[#b45309]",
  "bg-[#ecfccb] text-[#3f6212]",
  "bg-[#ffe4e6] text-[#be123c]",
  "bg-[#e0f2fe] text-[#0369a1]",
];

const freePlanCalendarUpgradeErrorNeedle = "Free plan workspaces can only schedule calendar assignments up to 30 days in advance.";

const TICKET_SEGMENT_PREFIX = "ticket:";

const calendarViewModeStorageKey = "kuii.calendar.viewMode";
const calendarSelectedTeamStorageKey = "kuii.calendar.selectedTeam";
const calendarMemberFilterStorageKey = "kuii.calendar.memberFilter";
const calendarTypeFilterStorageKey = "kuii.calendar.typeFilter";
const calendarShowScheduledTicketsStorageKey = "kuii.calendar.showScheduledTickets";
const calendarShowAssignmentsStorageKey = "kuii.calendar.showAssignments";
const calendarFocusedDateStorageKey = "kuii.calendar.focusedDate";

function readCalendarStoredValue(storageKey: string): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function getStoredCalendarViewMode(): CalendarViewMode {
  return readCalendarStoredValue(calendarViewModeStorageKey) === "month" ? "month" : "week";
}

function getStoredCalendarShowScheduledTickets(): boolean {
  return readCalendarStoredValue(calendarShowScheduledTicketsStorageKey) === "true";
}

function getStoredCalendarShowAssignments(): boolean {
  return readCalendarStoredValue(calendarShowAssignmentsStorageKey) !== "false";
}

function getStoredCalendarFocusedDate(timezone: string | null): Date {
  const storedValue = readCalendarStoredValue(calendarFocusedDateStorageKey);
  const match = storedValue ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(storedValue) : null;

  if (match) {
    return createCalendarDate(Number(match[1]), Number(match[2]), Number(match[3]));
  }

  return startOfDay(new Date(), timezone);
}

function isFreePlanCalendarUpgradeErrorMessage(message: string) {
  return message.includes(freePlanCalendarUpgradeErrorNeedle);
}

function createCalendarDate(year: number, month: number, day: number) {
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0));
}

function getCalendarDateKey(date: Date) {
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")}`;
}

function formatCalendarDateTimeInput(date: Date, hour: number, minute: number) {
  return `${getCalendarDateKey(date)}T${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function startOfDay(date = new Date(), timezone?: string | null) {
  const parts = getDateTimePartsInTimeZone(date, timezone);
  return createCalendarDate(parts.year, parts.month, parts.day);
}

function startOfWeek(date = new Date(), timezone?: string | null) {
  const nextDate = startOfDay(date, timezone);
  const dayOfWeek = nextDate.getUTCDay();
  const offset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  nextDate.setUTCDate(nextDate.getUTCDate() + offset);
  return nextDate;
}

function startOfMonth(date = new Date(), timezone?: string | null) {
  const parts = getDateTimePartsInTimeZone(date, timezone);
  return createCalendarDate(parts.year, parts.month, 1);
}

function addDays(date: Date, days: number) {
  const nextDate = new Date(date);
  nextDate.setUTCDate(nextDate.getUTCDate() + days);
  return nextDate;
}

function getDaysInMonth(date: Date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0, 12)).getUTCDate();
}

function createEmptyDraft(anchorDate: Date, defaultTeamId: string, defaultMemberEmail: string): AssignmentDraft {
  return {
    teamId: defaultTeamId,
    title: "",
    type: "On-call",
    memberEmail: defaultMemberEmail,
    startAt: formatCalendarDateTimeInput(anchorDate, 9, 0),
    endAt: formatCalendarDateTimeInput(anchorDate, 17, 0),
    notes: "",
  };
}

function getDefaultSelectedTeamId(currentUserEmail: string | null, currentUserRole: string | null, members: WorkspaceMember[], teams: UserTeam[]) {
  if (canManageWorkspaceAccess(currentUserRole)) {
    return "all";
  }

  if (!currentUserEmail) {
    return "all";
  }

  const currentMember = members.find(member => member.email.toLowerCase() === currentUserEmail.toLowerCase()) ?? null;

  if (!currentMember) {
    return "all";
  }

  const teamIdSet = new Set(teams.map(team => team.id));
  return currentMember.teamIds.find(teamId => teamIdSet.has(teamId)) ?? "all";
}

function createDraftFromAssignment(assignment: CalendarAssignment, timezone: string): AssignmentDraft {
  return {
    teamId: assignment.teamId,
    title: assignment.title,
    type: assignment.type,
    memberEmail: assignment.memberEmail,
    startAt: formatDateTimeForInput(assignment.startAt, timezone),
    endAt: formatDateTimeForInput(assignment.endAt, timezone),
    notes: assignment.notes,
  };
}

function normalizeAssignmentTypeName(value: string) {
  return value.trim();
}

function getAssignmentTypeBadgeClassName(type: CalendarAssignmentType) {
  const exactMatch = assignmentTypeBadgeClassNames[type];

  if (exactMatch) {
    return exactMatch;
  }

  const hash = [...type].reduce((total, character) => total + character.charCodeAt(0), 0);
  return customAssignmentTypeBadgeClassNames[hash % customAssignmentTypeBadgeClassNames.length] ?? customAssignmentTypeBadgeClassNames[0]!;
}

function getWeekDays(weekStart: Date) {
  return Array.from({ length: 7 }, (_, index) => addDays(weekStart, index));
}

function getMonthWeeks(monthStartDate: Date) {
  const gridStart = startOfWeek(monthStartDate);
  const days = Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  return Array.from({ length: 6 }, (_, index) => days.slice(index * 7, index * 7 + 7));
}

function getWeekRangeLabel(weekStartDate: Date, language: AppLanguage) {
  const weekEnd = addDays(weekStartDate, 6);
  const locale = getIntlLocale(language);
  const monthFormatter = new Intl.DateTimeFormat(locale, { month: "short", timeZone: "UTC" });
  const dayFormatter = new Intl.DateTimeFormat(locale, { day: "numeric", timeZone: "UTC" });
  const yearFormatter = new Intl.DateTimeFormat(locale, { year: "numeric", timeZone: "UTC" });

  const startMonth = monthFormatter.format(weekStartDate);
  const endMonth = monthFormatter.format(weekEnd);
  const startDay = dayFormatter.format(weekStartDate);
  const endDay = dayFormatter.format(weekEnd);
  const year = yearFormatter.format(weekEnd);

  return startMonth === endMonth
    ? `${startMonth} ${startDay}–${endDay}, ${year}`
    : `${startMonth} ${startDay} – ${endMonth} ${endDay}, ${year}`;
}

function getMonthRangeLabel(monthStartDate: Date, language: AppLanguage) {
  return new Intl.DateTimeFormat(getIntlLocale(language), { month: "long", year: "numeric", timeZone: "UTC" }).format(monthStartDate);
}

function getDayKey(date: Date) {
  return getCalendarDateKey(date);
}

function isSameMonth(left: Date, right: Date) {
  return left.getUTCFullYear() === right.getUTCFullYear() && left.getUTCMonth() === right.getUTCMonth();
}

function getDayStartInTimeZone(day: Date, timezone: string) {
  return normalizeDateTimeForStorage(`${getDayKey(day)}T00:00`, timezone);
}

function isAssignmentVisibleOnDay(assignment: CalendarAssignment, day: Date, timezone: string) {
  const dayStartValue = getDayStartInTimeZone(day, timezone);
  const dayEndValue = getDayStartInTimeZone(addDays(day, 1), timezone);

  if (!dayStartValue || !dayEndValue) {
    return false;
  }

  const dayStart = new Date(dayStartValue);
  const dayEnd = new Date(dayEndValue);
  const assignmentStart = new Date(assignment.startAt);
  const assignmentEnd = new Date(assignment.endAt);
  return assignmentStart < dayEnd && assignmentEnd > dayStart;
}

function overlapsRange(assignment: CalendarAssignment, rangeStart: Date, dayCount: number, timezone: string) {
  const rangeStartValue = getDayStartInTimeZone(rangeStart, timezone);
  const rangeEndValue = getDayStartInTimeZone(addDays(rangeStart, dayCount), timezone);

  if (!rangeStartValue || !rangeEndValue) {
    return false;
  }

  const rangeEnd = new Date(rangeEndValue);
  const assignmentStart = new Date(assignment.startAt);
  const assignmentEnd = new Date(assignment.endAt);
  return assignmentStart < rangeEnd && assignmentEnd > new Date(rangeStartValue);
}

function buildCalendarLanes(assignments: CalendarAssignment[], rangeStart: Date, dayCount: number, timezone: string) {
  const segments = assignments
    .flatMap<CalendarSegment>(assignment => {
      let startDayIndex = -1;
      let endDayIndex = -1;

      for (let dayIndex = 0; dayIndex < dayCount; dayIndex += 1) {
        if (!isAssignmentVisibleOnDay(assignment, addDays(rangeStart, dayIndex), timezone)) {
          continue;
        }

        if (startDayIndex === -1) {
          startDayIndex = dayIndex;
        }

        endDayIndex = dayIndex;
      }

      if (startDayIndex === -1 || endDayIndex === -1) {
        return [];
      }

      return [{
        assignment,
        startDayIndex,
        endDayIndex,
        span: endDayIndex - startDayIndex + 1,
        continuesBefore: startDayIndex === 0 && isAssignmentVisibleOnDay(assignment, addDays(rangeStart, -1), timezone),
        continuesAfter: endDayIndex === dayCount - 1 && isAssignmentVisibleOnDay(assignment, addDays(rangeStart, dayCount), timezone),
      }];
    })
    .sort((left, right) => {
      if (left.startDayIndex !== right.startDayIndex) {
        return left.startDayIndex - right.startDayIndex;
      }

      if (left.endDayIndex !== right.endDayIndex) {
        return right.endDayIndex - left.endDayIndex;
      }

      return left.assignment.title.localeCompare(right.assignment.title, undefined, { sensitivity: "base" });
    });

  const lanes: CalendarSegment[][] = [];

  for (const segment of segments) {
    const laneIndex = lanes.findIndex(lane => lane[lane.length - 1]!.endDayIndex < segment.startDayIndex);

    if (laneIndex === -1) {
      lanes.push([segment]);
      continue;
    }

    lanes[laneIndex]!.push(segment);
  }

  return lanes;
}

function SectionCard({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <section className={`rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)] ${className}`.trim()}>{children}</section>;
}

function CountSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function MetricCard({ label, value, detail, isLoading = false }: { label: string; value: string; detail: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_18px_44px_rgba(15,23,42,0.05)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{label}</p>
      <div className="mt-4 min-h-[2.25rem]">
        {isLoading ? <CountSpinner /> : <p className="text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

function LoadingSpinnerBadge({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-primary-ring)] bg-white/95 px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

export function CalendarView({
  currentUserEmail,
  currentUserRole,
  canCreateAssignments,
  canManageAssignmentTypes,
  isLoading = false,
  language,
  timezone,
  teamMembers,
  teams,
  assignmentTypes,
  assignments,
  tickets = [],
  customers = [],
  onOpenTicket,
  onLoadScheduledTickets,
  onLoadAssignmentTypeDefinitions,
  onCreateAssignmentType,
  onUpdateAssignmentType,
  onDeleteAssignmentType,
  onCreateAssignment,
  onUpdateAssignment,
  onDeleteAssignment,
}: CalendarViewProps) {
  const copy = calendarViewCopy[language];
  const activeMembers = useMemo(
    () => [...teamMembers].filter(member => member.status === "Active").sort((left, right) => left.name.localeCompare(right.name, getIntlLocale(language), { sensitivity: "base" })),
    [language, teamMembers],
  );
  const currentMember = useMemo(
    () => teamMembers.find(member => member.email.toLowerCase() === currentUserEmail?.toLowerCase()) ?? null,
    [currentUserEmail, teamMembers],
  );
  const currentUserTeams = useMemo(
    () => (currentMember?.teamIds ?? []).map(teamId => teams.find(team => team.id === teamId) ?? null).filter((team): team is UserTeam => team !== null),
    [currentMember?.teamIds, teams],
  );
  const canAccessAllTeams = useMemo(() => canManageWorkspaceAccess(currentUserRole), [currentUserRole]);
  const [viewMode, setViewMode] = useState<CalendarViewMode>(() => getStoredCalendarViewMode());
  const [focusedDate, setFocusedDate] = useState(() => getStoredCalendarFocusedDate(timezone));
  const [selectedTeamId, setSelectedTeamId] = useState<string>(() => readCalendarStoredValue(calendarSelectedTeamStorageKey) ?? getDefaultSelectedTeamId(currentUserEmail, currentUserRole, teamMembers, teams));
  const [memberFilter, setMemberFilter] = useState(() => readCalendarStoredValue(calendarMemberFilterStorageKey) ?? "all");
  const [typeFilter, setTypeFilter] = useState<"all" | CalendarAssignmentType>(() => (readCalendarStoredValue(calendarTypeFilterStorageKey) as "all" | CalendarAssignmentType | null) ?? "all");
  const [showScheduledTickets, setShowScheduledTickets] = useState(() => getStoredCalendarShowScheduledTickets());
  const [showAssignments, setShowAssignments] = useState(() => getStoredCalendarShowAssignments());
  const [isLoadingScheduledTickets, setIsLoadingScheduledTickets] = useState(false);
  const [hasLoadedScheduledTickets, setHasLoadedScheduledTickets] = useState(false);
  const [hoveredSegmentId, setHoveredSegmentId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAssignmentTypeModalOpen, setIsAssignmentTypeModalOpen] = useState(false);
  const [editingAssignmentId, setEditingAssignmentId] = useState<string | null>(null);
  const [assignmentTypeDefinitions, setAssignmentTypeDefinitions] = useState<CalendarAssignmentTypeDefinition[]>([]);
  const [assignmentTypeTeamId, setAssignmentTypeTeamId] = useState("");
  const [editingAssignmentTypeId, setEditingAssignmentTypeId] = useState<string | null>(null);
  const [editingAssignmentTypeName, setEditingAssignmentTypeName] = useState("");
  const [newAssignmentTypeName, setNewAssignmentTypeName] = useState("");
  const [assignmentTypeError, setAssignmentTypeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [assignmentTypeBusy, setAssignmentTypeBusy] = useState(false);

  const weekStart = useMemo(() => startOfWeek(focusedDate, timezone), [focusedDate, timezone]);
  const monthStart = useMemo(() => startOfMonth(focusedDate, timezone), [focusedDate, timezone]);
  const weekDays = useMemo(() => getWeekDays(weekStart), [weekStart]);
  const monthWeeks = useMemo(() => getMonthWeeks(monthStart), [monthStart]);
  const monthDays = useMemo(() => monthWeeks.flat(), [monthWeeks]);
  const visibleRangeStart = viewMode === "week" ? weekStart : monthStart;
  const visibleRangeDayCount = viewMode === "week" ? 7 : getDaysInMonth(monthStart);
  const visibleRangeLabel = viewMode === "week" ? getWeekRangeLabel(weekStart, language) : getMonthRangeLabel(monthStart, language);
  const dayNameFormatter = useMemo(() => new Intl.DateTimeFormat(getIntlLocale(language), { weekday: "short", timeZone: "UTC" }), [language]);
  const dayNumberFormatter = useMemo(() => new Intl.DateTimeFormat(getIntlLocale(language), { day: "numeric", timeZone: "UTC" }), [language]);
  const monthDayFormatter = useMemo(() => new Intl.DateTimeFormat(getIntlLocale(language), { month: "short", day: "numeric", timeZone: "UTC" }), [language]);
  const availableTeamOptions = useMemo(
    () => (canAccessAllTeams ? teams : currentUserTeams),
    [canAccessAllTeams, currentUserTeams, teams],
  );
  const manageableTeamOptions = availableTeamOptions;
  const selectedTeamMembers = useMemo(
    () => ((selectedTeamId === "all" && (canAccessAllTeams || currentUserTeams.length === 0))
      ? activeMembers
      : activeMembers.filter(member => member.teamIds.includes(selectedTeamId))),
    [activeMembers, canAccessAllTeams, currentUserTeams.length, selectedTeamId],
  );
  const filteredAssignmentTypeDefinitions = useMemo(
    () => assignmentTypeDefinitions.filter(definition => definition.teamId === assignmentTypeTeamId),
    [assignmentTypeDefinitions, assignmentTypeTeamId],
  );
  const defaultDraftTeamId = useMemo(() => {
    if (selectedTeamId !== "all") {
      return selectedTeamId;
    }

    return availableTeamOptions[0]?.id ?? "";
  }, [availableTeamOptions, selectedTeamId]);
  const [draft, setDraft] = useState<AssignmentDraft>(() => createEmptyDraft(startOfWeek(new Date(), timezone), defaultDraftTeamId, ""));
  const draftTeamId = draft.teamId || defaultDraftTeamId;
  const draftTeamMembers = useMemo(
    () => (draftTeamId ? activeMembers.filter(member => member.teamIds.includes(draftTeamId)) : []),
    [activeMembers, draftTeamId],
  );
  const defaultMemberEmail = selectedTeamMembers.find(member => member.email.toLowerCase() === currentUserEmail?.toLowerCase())?.email ?? selectedTeamMembers[0]?.email ?? "";
  const defaultDraftMemberEmail = draftTeamMembers.find(member => member.email.toLowerCase() === currentUserEmail?.toLowerCase())?.email ?? draftTeamMembers[0]?.email ?? "";

  useEffect(() => {
    setFocusedDate(currentDate => startOfDay(currentDate, timezone));
  }, [timezone]);

  useEffect(() => {
    if (canAccessAllTeams) {
      const allowedTeamIds = new Set(teams.map(team => team.id));
      setSelectedTeamId(currentTeamId => (currentTeamId === "all" || allowedTeamIds.has(currentTeamId) ? currentTeamId : "all"));
      return;
    }

    if (currentUserTeams.length === 0) {
      setSelectedTeamId(currentTeamId => (currentTeamId === "all" ? currentTeamId : "all"));
      return;
    }

    const allowedTeamIds = new Set(currentUserTeams.map(team => team.id));
    setSelectedTeamId(currentTeamId => (allowedTeamIds.has(currentTeamId) ? currentTeamId : currentUserTeams[0]!.id));
  }, [canAccessAllTeams, currentUserTeams, teams]);

  useEffect(() => {
    setMemberFilter(currentFilter => (currentFilter === "all" || selectedTeamMembers.some(member => member.email === currentFilter) ? currentFilter : "all"));
  }, [selectedTeamMembers]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(calendarViewModeStorageKey, viewMode);
    } catch {
      // Ignore persistence failures (e.g. storage disabled).
    }
  }, [viewMode]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(calendarFocusedDateStorageKey, getCalendarDateKey(focusedDate));
    } catch {
      // Ignore persistence failures (e.g. storage disabled).
    }
  }, [focusedDate]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(calendarSelectedTeamStorageKey, selectedTeamId);
    } catch {
      // Ignore persistence failures (e.g. storage disabled).
    }
  }, [selectedTeamId]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(calendarMemberFilterStorageKey, memberFilter);
    } catch {
      // Ignore persistence failures (e.g. storage disabled).
    }
  }, [memberFilter]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(calendarTypeFilterStorageKey, typeFilter);
    } catch {
      // Ignore persistence failures (e.g. storage disabled).
    }
  }, [typeFilter]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(calendarShowScheduledTicketsStorageKey, showScheduledTickets ? "true" : "false");
    } catch {
      // Ignore persistence failures (e.g. storage disabled).
    }
  }, [showScheduledTickets]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(calendarShowAssignmentsStorageKey, showAssignments ? "true" : "false");
    } catch {
      // Ignore persistence failures (e.g. storage disabled).
    }
  }, [showAssignments]);

  useEffect(() => {
    setAssignmentTypeTeamId(currentTeamId => {
      const validTeamIds = new Set(manageableTeamOptions.map(team => team.id));

      if (currentTeamId && validTeamIds.has(currentTeamId)) {
        return currentTeamId;
      }

      if (selectedTeamId !== "all" && validTeamIds.has(selectedTeamId)) {
        return selectedTeamId;
      }

      return manageableTeamOptions[0]?.id ?? "";
    });
  }, [manageableTeamOptions, selectedTeamId]);

  useEffect(() => {
    setDraft(currentDraft => {
      const validTeamIds = new Set(availableTeamOptions.map(team => team.id));
      const nextTeamId = currentDraft.teamId && validTeamIds.has(currentDraft.teamId) ? currentDraft.teamId : defaultDraftTeamId;
      const nextTeamMembers = nextTeamId ? activeMembers.filter(member => member.teamIds.includes(nextTeamId)) : [];
      const nextMemberEmail = nextTeamMembers.find(member => member.email.toLowerCase() === currentUserEmail?.toLowerCase())?.email ?? nextTeamMembers[0]?.email ?? "";

      if (!currentDraft.teamId && nextTeamId) {
        return {
          ...currentDraft,
          teamId: nextTeamId,
          memberEmail: currentDraft.memberEmail || nextMemberEmail,
        };
      }

      if (!currentDraft.memberEmail && nextMemberEmail) {
        return { ...currentDraft, memberEmail: nextMemberEmail };
      }

      if (
        currentDraft.teamId === nextTeamId
        && currentDraft.memberEmail
        && nextTeamMembers.some(member => member.email === currentDraft.memberEmail)
      ) {
        return currentDraft;
      }

      return {
        ...currentDraft,
        teamId: nextTeamId,
        memberEmail: nextMemberEmail,
      };
    });
  }, [activeMembers, availableTeamOptions, currentUserEmail, defaultDraftTeamId]);

  const filteredAssignments = useMemo(() => {
    return assignments.filter(assignment => {
      const teamMatches = selectedTeamId === "all" || assignment.teamId === selectedTeamId;
      const memberMatches = memberFilter === "all" || assignment.memberEmail === memberFilter;
      const typeMatches = typeFilter === "all" || assignment.type === typeFilter;
      return teamMatches && memberMatches && typeMatches;
    });
  }, [assignments, memberFilter, selectedTeamId, typeFilter]);

  const visibleAssignments = useMemo(
    () => filteredAssignments.filter(assignment => overlapsRange(assignment, visibleRangeStart, visibleRangeDayCount, timezone)),
    [filteredAssignments, timezone, visibleRangeDayCount, visibleRangeStart],
  );
  const visibleDays = viewMode === "week" ? weekDays : monthDays.filter(day => isSameMonth(day, monthStart));
  const weekLanes = useMemo(() => buildCalendarLanes(showAssignments ? visibleAssignments : [], weekStart, 7, timezone), [showAssignments, timezone, visibleAssignments, weekStart]);
  const uniqueScheduledMembers = new Set(visibleAssignments.map(assignment => assignment.memberEmail));
  const coveredOnCallDays = visibleDays.filter(day => visibleAssignments.some(assignment => assignment.type === "On-call" && isAssignmentVisibleOnDay(assignment, day, timezone))).length;
  const unscheduledMembers = selectedTeamMembers.filter(member => !uniqueScheduledMembers.has(member.email));
  const rotationCount = visibleAssignments.filter(assignment => assignment.type === "Task rotation").length;

  const ticketCalendarMeta = useMemo(() => {
    const map = new Map<string, { ticket: Ticket; customer: Customer | null }>();
    if (!showScheduledTickets) {
      return map;
    }
    for (const ticket of tickets) {
      if (!ticket.scheduleAt) {
        continue;
      }
      const customer = customers.find(item => item.id === ticket.customerId) ?? null;
      map.set(`${TICKET_SEGMENT_PREFIX}${ticket.id}`, { ticket, customer });
    }
    return map;
  }, [customers, showScheduledTickets, tickets]);
  const ticketAssignments = useMemo<CalendarAssignment[]>(() => {
    if (!showScheduledTickets) {
      return [];
    }
    return tickets.flatMap(ticket => {
      if (!ticket.scheduleAt) {
        return [];
      }
      const end = ticket.scheduleAt;
      const start = ticket.scheduleStartAt ?? end;
      return [{
        id: `${TICKET_SEGMENT_PREFIX}${ticket.id}`,
        teamId: "",
        title: ticket.subject,
        type: ticket.category as CalendarAssignmentType,
        memberEmail: "",
        startAt: start <= end ? start : end,
        endAt: end >= start ? end : start,
        notes: "",
      }];
    });
  }, [showScheduledTickets, tickets]);
  const visibleTicketAssignments = useMemo(
    () => ticketAssignments.filter(assignment => overlapsRange(assignment, visibleRangeStart, visibleRangeDayCount, timezone)),
    [ticketAssignments, timezone, visibleRangeDayCount, visibleRangeStart],
  );
  const weekTicketLanes = useMemo(() => buildCalendarLanes(visibleTicketAssignments, weekStart, 7, timezone), [timezone, visibleTicketAssignments, weekStart]);

  const handleToggleScheduledTickets = useCallback(async () => {
    const next = !showScheduledTickets;
    setShowScheduledTickets(next);
    if (next && !hasLoadedScheduledTickets && onLoadScheduledTickets) {
      setIsLoadingScheduledTickets(true);
      try {
        await onLoadScheduledTickets();
        setHasLoadedScheduledTickets(true);
      } finally {
        setIsLoadingScheduledTickets(false);
      }
    }
  }, [hasLoadedScheduledTickets, onLoadScheduledTickets, showScheduledTickets]);

  useEffect(() => {
    if (!showScheduledTickets || hasLoadedScheduledTickets || !onLoadScheduledTickets) {
      return;
    }

    let cancelled = false;
    setIsLoadingScheduledTickets(true);
    void (async () => {
      try {
        await onLoadScheduledTickets();
        if (!cancelled) {
          setHasLoadedScheduledTickets(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingScheduledTickets(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [hasLoadedScheduledTickets, onLoadScheduledTickets, showScheduledTickets]);

  const resetDraft = (nextDate = visibleRangeStart, nextTeamId = defaultDraftTeamId) => {
    const nextTeamMembers = nextTeamId ? activeMembers.filter(member => member.teamIds.includes(nextTeamId)) : [];
    const nextMemberEmail = nextTeamMembers.find(member => member.email.toLowerCase() === currentUserEmail?.toLowerCase())?.email ?? nextTeamMembers[0]?.email ?? "";
    setDraft(createEmptyDraft(nextDate, nextTeamId, nextMemberEmail));
    setEditingAssignmentId(null);
    setError(null);
  };

  const handleOpenAssignmentTypeModal = async () => {
    setAssignmentTypeBusy(true);
    setAssignmentTypeError(null);

    try {
      const definitions = await onLoadAssignmentTypeDefinitions();
      setAssignmentTypeDefinitions(definitions);
      setEditingAssignmentTypeId(null);
      setEditingAssignmentTypeName("");
      setNewAssignmentTypeName("");
      setIsAssignmentTypeModalOpen(true);
    } catch (loadError) {
      setAssignmentTypeError(loadError instanceof Error ? loadError.message : copy.errorLoadTypes);
      setIsAssignmentTypeModalOpen(true);
    } finally {
      setAssignmentTypeBusy(false);
    }
  };

  const handleCloseAssignmentTypeModal = () => {
    if (assignmentTypeBusy) {
      return;
    }

    setIsAssignmentTypeModalOpen(false);
    setAssignmentTypeDefinitions([]);
    setEditingAssignmentTypeId(null);
    setEditingAssignmentTypeName("");
    setNewAssignmentTypeName("");
    setAssignmentTypeError(null);
  };

  const handleStartEditingAssignmentType = (assignmentType: CalendarAssignmentTypeDefinition) => {
    setEditingAssignmentTypeId(assignmentType.id);
    setEditingAssignmentTypeName(assignmentType.name);
    setAssignmentTypeError(null);
  };

  const handleCancelEditingAssignmentType = () => {
    setEditingAssignmentTypeId(null);
    setEditingAssignmentTypeName("");
    setAssignmentTypeError(null);
  };

  const handleOpenCreate = (nextDate = visibleRangeStart) => {
    if (!canCreateAssignments) {
      return;
    }

    resetDraft(nextDate);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (assignment: CalendarAssignment) => {
    if (!canCreateAssignments) {
      return;
    }

    setDraft(createDraftFromAssignment(assignment, timezone));
    setEditingAssignmentId(assignment.id);
    setError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetDraft();
    setBusy(false);
  };

  const handleSave = async () => {
    if (!draft.teamId || !draft.title.trim() || !draft.memberEmail || !draft.startAt || !draft.endAt) {
      setError(copy.errorRequired);
      return;
    }

    const normalizedStartAt = normalizeDateTimeForStorage(draft.startAt, timezone);
    const normalizedEndAt = normalizeDateTimeForStorage(draft.endAt, timezone);

    if (!normalizedStartAt || !normalizedEndAt) {
      setError(copy.errorInvalidTime);
      return;
    }

    if (new Date(normalizedEndAt).getTime() <= new Date(normalizedStartAt).getTime()) {
      setError(copy.errorEndBeforeStart);
      return;
    }

    setBusy(true);
    setError(null);

    const payload: CalendarAssignmentInput = {
      teamId: draft.teamId,
      title: draft.title.trim(),
      type: draft.type,
      memberEmail: draft.memberEmail,
      startAt: normalizedStartAt,
      endAt: normalizedEndAt,
      notes: draft.notes.trim(),
    };

    try {
      if (editingAssignmentId) {
        await onUpdateAssignment(editingAssignmentId, payload);
      } else {
        await onCreateAssignment(payload);
      }

      handleCloseModal();
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : copy.errorSaveAssignment;

      if (isFreePlanCalendarUpgradeErrorMessage(message)) {
        window.alert(copy.errorUpgradeFutureEvents);
        setError(copy.errorUpgradeFutureEvents);
      } else {
        setError(message);
      }

      setBusy(false);
    }
  };

  const handleCreateAssignmentType = async () => {
    const normalizedType = normalizeAssignmentTypeName(newAssignmentTypeName);

    if (!normalizedType) {
      setAssignmentTypeError(copy.errorEnterNewType);
      return;
    }

    if (!assignmentTypeTeamId) {
      setAssignmentTypeError(copy.errorSelectTeamFirst);
      return;
    }

    if (filteredAssignmentTypeDefinitions.some(type => type.name.toLowerCase() === normalizedType.toLowerCase())) {
      setAssignmentTypeError(copy.errorTypeExists);
      return;
    }

    setAssignmentTypeBusy(true);
    setAssignmentTypeError(null);

    try {
      const savedDefinitions = await onCreateAssignmentType(assignmentTypeTeamId, normalizedType);
      setAssignmentTypeDefinitions(savedDefinitions);
      setDraft(current => ({ ...current, type: normalizedType }));
      setNewAssignmentTypeName("");
    } catch (createError) {
      setAssignmentTypeError(createError instanceof Error ? createError.message : copy.errorCreateType);
    } finally {
      setAssignmentTypeBusy(false);
    }
  };

  const handleSaveAssignmentTypeEdit = async (assignmentType: CalendarAssignmentTypeDefinition) => {
    const normalizedType = normalizeAssignmentTypeName(editingAssignmentTypeName);

    if (!normalizedType) {
      setAssignmentTypeError(copy.errorEnterTypeName);
      return;
    }

    if (filteredAssignmentTypeDefinitions.some(type => type.id !== assignmentType.id && type.name.toLowerCase() === normalizedType.toLowerCase())) {
      setAssignmentTypeError(copy.errorTypeExists);
      return;
    }

    setAssignmentTypeBusy(true);
    setAssignmentTypeError(null);

    try {
      const savedDefinitions = await onUpdateAssignmentType(assignmentType, normalizedType);
      setAssignmentTypeDefinitions(savedDefinitions);
      setDraft(current => (current.type.toLowerCase() === assignmentType.name.toLowerCase() ? { ...current, type: normalizedType } : current));
      setTypeFilter(current => (current !== "all" && current.toLowerCase() === assignmentType.name.toLowerCase() ? normalizedType : current));
      handleCancelEditingAssignmentType();
    } catch (updateError) {
      setAssignmentTypeError(updateError instanceof Error ? updateError.message : copy.errorUpdateType);
    } finally {
      setAssignmentTypeBusy(false);
    }
  };

  const handleDeleteAssignmentType = async (assignmentType: CalendarAssignmentTypeDefinition) => {
    if (!window.confirm(copy.confirmDeleteType(assignmentType.name))) {
      return;
    }

    setAssignmentTypeBusy(true);
    setAssignmentTypeError(null);

    try {
      const savedDefinitions = await onDeleteAssignmentType(assignmentType.id);
      setAssignmentTypeDefinitions(savedDefinitions);
      setDraft(current => (current.type.toLowerCase() === assignmentType.name.toLowerCase() ? { ...current, type: assignmentTypes[0] ?? "On-call" } : current));
      setTypeFilter(current => (current !== "all" && current.toLowerCase() === assignmentType.name.toLowerCase() ? "all" : current));

      if (editingAssignmentTypeId === assignmentType.id) {
        handleCancelEditingAssignmentType();
      }
    } catch (deleteError) {
      setAssignmentTypeError(deleteError instanceof Error ? deleteError.message : copy.errorDeleteType);
    } finally {
      setAssignmentTypeBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!editingAssignmentId) {
      return;
    }

    if (!window.confirm(copy.confirmDeleteAssignment)) {
      return;
    }

    setBusy(true);
    setError(null);

    try {
      await onDeleteAssignment(editingAssignmentId);
      handleCloseModal();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : copy.errorDeleteAssignment);
      setBusy(false);
    }
  };

  const getAssignmentTypeLabel = (type: CalendarAssignmentType) => copy.assignmentTypeLabels[type as keyof typeof copy.assignmentTypeLabels] ?? type;

  const shiftVisibleRange = (direction: -1 | 1) => {
    setFocusedDate(currentDate => {
      const nextDate = new Date(currentDate);

      if (viewMode === "week") {
        nextDate.setUTCDate(nextDate.getUTCDate() + direction * 7);
      } else {
        nextDate.setUTCMonth(nextDate.getUTCMonth() + direction, 1);
      }

      return startOfDay(nextDate, timezone);
    });
  };

  const handleResetToToday = () => {
    setFocusedDate(startOfDay(new Date(), timezone));
  };

  const renderAssignmentBar = (segment: CalendarSegment, compact = false) => {
    const isHovered = hoveredSegmentId === segment.assignment.id;
    const continuationClassName = `${segment.continuesBefore ? "rounded-l-none border-l-0" : ""} ${segment.continuesAfter ? "rounded-r-none border-r-0" : ""}`;
    const hoverHandlers = {
      onMouseEnter: () => setHoveredSegmentId(segment.assignment.id),
      onMouseLeave: () => setHoveredSegmentId(current => (current === segment.assignment.id ? null : current)),
      onFocus: () => setHoveredSegmentId(segment.assignment.id),
      onBlur: () => setHoveredSegmentId(current => (current === segment.assignment.id ? null : current)),
    };
    const sharedShadowClassName = isHovered ? "shadow-[0_18px_38px_rgba(15,23,42,0.12)]" : "shadow-[0_12px_26px_rgba(15,23,42,0.08)]";

    if (segment.assignment.id.startsWith(TICKET_SEGMENT_PREFIX)) {
      const meta = ticketCalendarMeta.get(segment.assignment.id);
      const customerLabel = meta?.customer ? (meta.customer.company || meta.customer.name) : copy.ticketNoCustomer;

      return (
        <button
          key={segment.assignment.id}
          type="button"
          {...hoverHandlers}
          onClick={() => meta && onOpenTicket?.(meta.ticket.id)}
          disabled={!meta || !onOpenTicket}
          className={`calendar-assignment-bar calendar-assignment-bar--ticket ${isHovered ? "calendar-assignment-bar--active" : ""} pointer-events-auto flex items-center gap-3 overflow-hidden rounded-2xl border border-dashed px-3 text-left transition ${isHovered ? "border-[var(--brand-primary)] bg-[var(--brand-primary-soft-strong)]" : "border-[#d0d5dd] bg-[var(--brand-primary-soft)]"} ${sharedShadowClassName} ${continuationClassName} ${compact ? "h-10 py-2" : "h-12 py-2.5"}`}
          style={{ gridColumn: `${segment.startDayIndex + 1} / span ${segment.span}` }}
        >
          {!segment.continuesBefore ? (
            <span
              className="calendar-ticket-badge inline-flex max-w-[9rem] shrink-0 items-center gap-1.5 rounded-full bg-[var(--brand-primary-soft-strong)] px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--brand-primary)]"
              title={copy.ticketBadge}
            >
              <svg viewBox="0 0 20 20" fill="none" className="h-3.5 w-3.5 shrink-0" aria-hidden="true">
                <path d="M5 4.75h10A1.25 1.25 0 0 1 16.25 6v2a1.75 1.75 0 0 0 0 3.5v2A1.25 1.25 0 0 1 15 14.75H5A1.25 1.25 0 0 1 3.75 13.5v-2a1.75 1.75 0 0 0 0-3.5V6A1.25 1.25 0 0 1 5 4.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                <path d="M8 7.5h4M8 10h4M8 12.5h2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span className="sr-only">{copy.ticketBadge}</span>
              <span className="truncate">{segment.assignment.type}</span>
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-[#101828]">{segment.assignment.title || copy.untitledAssignment}</p>
            <p className="truncate text-[11px] text-[#667085]">{customerLabel}</p>
          </div>
          {!compact ? <span className="shrink-0 text-[11px] font-semibold text-[#98a2b3]">{segment.span}{copy.dayUnitShort}</span> : null}
        </button>
      );
    }

    const member = resolveWorkspaceMemberByEmail(teamMembers, segment.assignment.memberEmail);
    const isCurrentUser = segment.assignment.memberEmail.toLowerCase() === currentUserEmail?.toLowerCase();

    return (
      <button
        key={segment.assignment.id}
        type="button"
        {...hoverHandlers}
        onClick={() => handleOpenEdit(segment.assignment)}
        disabled={!canCreateAssignments}
        className={`calendar-assignment-bar ${isHovered ? "calendar-assignment-bar--active" : ""} pointer-events-auto flex items-center gap-3 overflow-hidden rounded-2xl border px-3 text-left transition ${isHovered ? "border-[var(--brand-primary)]" : "border-transparent"} ${sharedShadowClassName} ${continuationClassName} ${compact ? "h-10 py-2" : "h-12 py-2.5"}`}
        style={{ gridColumn: `${segment.startDayIndex + 1} / span ${segment.span}` }}
      >
        {!segment.continuesBefore ? (
          <span className={`shrink-0 rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${getAssignmentTypeBadgeClassName(segment.assignment.type)}`}>
            {getAssignmentTypeLabel(segment.assignment.type)}
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-[#101828]">{segment.assignment.title || copy.untitledAssignment}</p>
          <p className="truncate text-[11px] text-[#667085]">{member?.name ?? segment.assignment.memberEmail}{isCurrentUser ? copy.youSuffix : ""}</p>
        </div>
        {!compact ? <span className="shrink-0 text-[11px] font-semibold text-[#98a2b3]">{segment.span}{copy.dayUnitShort}</span> : null}
      </button>
    );
  };

  const renderWeekCalendar = () => {
    const weekBoardHeight = Math.max(220, 84 + (weekLanes.length + weekTicketLanes.length) * 56);

    return (
      <div className="mt-6 overflow-x-auto rounded-[28px] border border-[#eaecf0] bg-[#fcfcfd]">
        <div className="min-w-[980px]">
          <div className="grid grid-cols-7 border-b border-[#eaecf0] bg-[#f8fafc]">
            {weekDays.map(day => {
              const dayAssignments = visibleAssignments.filter(assignment => isAssignmentVisibleOnDay(assignment, day, timezone));

              return (
                <div key={`header-${getDayKey(day)}`} className="border-r border-[#eaecf0] px-4 py-4 last:border-r-0">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{dayNameFormatter.format(day)}</p>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <p className="text-lg font-bold text-[#101828]">{monthDayFormatter.format(day)}</p>
                      {canCreateAssignments ? <button type="button" onClick={() => handleOpenCreate(day)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--brand-primary-soft)] p-0 text-[16px] font-semibold leading-none text-[var(--brand-primary)] transition hover:bg-[var(--brand-primary-soft-strong)]"><span className="-translate-y-px">+</span></button> : null}
                    </div>
                  </div>
                  <div className="mt-2 flex items-center">
                    <p className="text-[11px] font-semibold text-[#667085]">{copy.assignmentsCount(dayAssignments.length)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="relative">
            <div className="grid grid-cols-7">
              {weekDays.map(day => (
                <div key={getDayKey(day)} className="border-r border-[#eaecf0] last:border-r-0" style={{ minHeight: weekBoardHeight }} />
              ))}
            </div>

            <div className="pointer-events-none absolute inset-x-0 top-3 px-2 pb-3">
              <div className="relative">
                {isLoading ? (
                  <div className="absolute inset-x-0 top-0 z-[1] flex justify-center">
                    <div className="flex h-[120px] w-full max-w-[420px] items-center justify-center rounded-[20px] border border-dashed border-[#d0d5dd] bg-white/88 text-sm text-[#98a2b3] shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
                      <LoadingSpinnerBadge label={copy.loadingCalendar} />
                    </div>
                  </div>
                ) : null}

                <div className="space-y-3">
                  {weekLanes.length > 0 || weekTicketLanes.length > 0 ? (
                  <>
                    {weekLanes.map((lane, laneIndex) => (
                      <div key={`week-lane-${laneIndex}`} className="grid grid-cols-7 gap-2">
                        {lane.map(segment => renderAssignmentBar(segment))}
                      </div>
                    ))}
                    {weekTicketLanes.map((lane, laneIndex) => (
                      <div key={`week-ticket-lane-${laneIndex}`} className="grid grid-cols-7 gap-2">
                        {lane.map(segment => renderAssignmentBar(segment))}
                      </div>
                    ))}
                  </>
                ) : (
                  <div className="flex h-[120px] items-center justify-center rounded-[20px] border border-dashed border-[#d0d5dd] bg-white/80 text-sm text-[#98a2b3]">
                    {copy.noAssignmentsThisWeek}
                  </div>
                )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMonthCalendar = () => {
    return (
      <div className="mt-6 overflow-x-auto rounded-[28px] border border-[#eaecf0] bg-[#fcfcfd]">
        <div className="min-w-[980px]">
          <div className="grid grid-cols-7 border-b border-[#eaecf0] bg-[#f8fafc]">
            {weekDays.map(day => (
              <div key={`month-header-${getDayKey(day)}`} className="border-r border-[#eaecf0] px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3] last:border-r-0">
                {dayNameFormatter.format(day)}
              </div>
            ))}
          </div>

          {isLoading ? (
            <div className="pointer-events-none absolute inset-x-0 top-4 z-[1] flex justify-center px-4">
              <LoadingSpinnerBadge label={copy.loadingCalendar} />
            </div>
          ) : null}

          {monthWeeks.map((week, weekIndex) => {
            const rowStart = week[0]!;
            const rowAssignments = filteredAssignments.filter(assignment => overlapsRange(assignment, rowStart, 7, timezone));
            const rowLanes = buildCalendarLanes(showAssignments ? rowAssignments : [], rowStart, 7, timezone);
            const rowTicketAssignments = visibleTicketAssignments.filter(assignment => overlapsRange(assignment, rowStart, 7, timezone));
            const rowTicketLanes = buildCalendarLanes(rowTicketAssignments, rowStart, 7, timezone);
            const rowHeight = Math.max(164, 56 + (rowLanes.length + rowTicketLanes.length) * 48);

            return (
              <div key={`month-week-${weekIndex}`} className="relative border-b border-[#eaecf0] last:border-b-0">
                <div className="grid grid-cols-7">
                  {week.map(day => {
                    const dayAssignments = rowAssignments.filter(assignment => isAssignmentVisibleOnDay(assignment, day, timezone));
                    const isCurrentMonth = isSameMonth(day, monthStart);

                    return (
                      <div
                        key={getDayKey(day)}
                        className={`border-r border-[#eaecf0] px-3 py-3 last:border-r-0 ${isCurrentMonth ? "bg-white" : "bg-[#f8fafc]"}`}
                        style={{ minHeight: rowHeight }}
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2">
                            <p className={`inline-flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-sm font-semibold ${isCurrentMonth ? "bg-[#f3f4f6] text-[#101828]" : "bg-transparent text-[#98a2b3]"}`}>
                              {dayNumberFormatter.format(day)}
                            </p>
                            <p className="text-[11px] text-[#98a2b3]">{copy.itemsCount(dayAssignments.length)}</p>
                            {canCreateAssignments ? <button type="button" onClick={() => handleOpenCreate(day)} className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--brand-primary-soft)] p-0 text-[14px] font-semibold leading-none text-[var(--brand-primary)] transition hover:bg-[var(--brand-primary-soft-strong)]"><span className="-translate-y-px">+</span></button> : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="pointer-events-none absolute inset-x-0 top-[3.4rem] px-2 pb-3">
                  <div className="space-y-2">
                    {rowLanes.map((lane, laneIndex) => (
                      <div key={`month-row-${weekIndex}-lane-${laneIndex}`} className="grid grid-cols-7 gap-2">
                        {lane.map(segment => renderAssignmentBar(segment, true))}
                      </div>
                    ))}
                    {rowTicketLanes.map((lane, laneIndex) => (
                      <div key={`month-row-${weekIndex}-ticket-lane-${laneIndex}`} className="grid grid-cols-7 gap-2">
                        {lane.map(segment => renderAssignmentBar(segment, true))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6 p-8">
      <section>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.pageEyebrow}</p>
            <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.pageTitle}</h1>
            <p className="mt-3 max-w-3xl text-sm text-[#667085]">{copy.pageDescription}</p>
          </div>

          {canManageAssignmentTypes ? (
            <div className="flex items-center gap-3">
              <button type="button" className={primaryButtonClassName} onClick={() => void handleOpenAssignmentTypeModal()}>
                {copy.editAssignmentTypes}
              </button>
            </div>
          ) : null}
        </div>
      </section>

      <section className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label={copy.scheduledThis(viewMode)} value={String(visibleAssignments.length).padStart(2, "0")} detail={copy.assignmentsVisibleIn(viewMode)} isLoading={isLoading} />
        <MetricCard label={copy.membersCovered} value={String(uniqueScheduledMembers.size).padStart(2, "0")} detail={copy.membersCoveredDetail} isLoading={isLoading} />
        <MetricCard label={copy.onCallCoverage} value={`${coveredOnCallDays}/${visibleDays.length}`} detail={viewMode === "week" ? copy.onCallCoverageWeekDetail : copy.onCallCoverageMonthDetail} isLoading={isLoading} />
        <MetricCard label={copy.rotationsActive} value={String(rotationCount).padStart(2, "0")} detail={copy.rotationsActiveDetail(viewMode)} isLoading={isLoading} />
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.9fr)_minmax(260px,0.62fr)]">
        <SectionCard>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.calendarView}</p>
              <h2 className="mt-3 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{visibleRangeLabel}</h2>
              <p className="mt-2 text-sm text-[#667085]">{copy.calendarViewDescription}</p>
            </div>

            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={handleToggleScheduledTickets}
                disabled={isLoadingScheduledTickets}
                aria-pressed={showScheduledTickets}
                aria-busy={isLoadingScheduledTickets}
                title={showScheduledTickets ? copy.hideScheduledTickets : copy.showScheduledTickets}
                aria-label={showScheduledTickets ? copy.hideScheduledTickets : copy.showScheduledTickets}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl transition disabled:cursor-not-allowed disabled:opacity-70 ${showScheduledTickets ? "brand-primary-button" : "border border-[var(--brand-primary)] bg-white text-[var(--brand-primary)] hover:bg-[var(--brand-primary-soft)]"}`}
              >
                {isLoadingScheduledTickets ? (
                  <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5 animate-spin" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" className="opacity-25" />
                    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden="true">
                    <path d="M5 4.75h10A1.25 1.25 0 0 1 16.25 6v2a1.75 1.75 0 0 0 0 3.5v2A1.25 1.25 0 0 1 15 14.75H5A1.25 1.25 0 0 1 3.75 13.5v-2a1.75 1.75 0 0 0 0-3.5V6A1.25 1.25 0 0 1 5 4.75Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    <path d="M8 7.5h4M8 10h4M8 12.5h2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowAssignments(current => !current)}
                aria-pressed={showAssignments}
                title={showAssignments ? copy.hideTeamSchedule : copy.showTeamSchedule}
                aria-label={showAssignments ? copy.hideTeamSchedule : copy.showTeamSchedule}
                className={`inline-flex h-10 w-10 items-center justify-center rounded-2xl transition ${showAssignments ? "brand-primary-button" : "border border-[var(--brand-primary)] bg-white text-[var(--brand-primary)] hover:bg-[var(--brand-primary-soft)]"}`}
              >
                <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden="true">
                  <circle cx="10" cy="7" r="2.75" stroke="currentColor" strokeWidth="1.5" />
                  <path d="M4.75 16.25a5.25 5.25 0 0 1 10.5 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {canCreateAssignments ? <button type="button" className={primaryButtonClassName} onClick={() => handleOpenCreate()}>{copy.scheduleAssignment}</button> : null}
            </div>

            <div className="flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between">
              <div className="flex flex-wrap items-center gap-3">
                <div className="calendar-view-toggle flex items-center rounded-2xl p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("week")}
                    className={`calendar-view-toggle-button rounded-xl px-4 py-2 text-sm font-semibold transition ${viewMode === "week" ? "calendar-view-toggle-button--active" : ""}`}
                  >
                    {copy.weekly}
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("month")}
                    className={`calendar-view-toggle-button rounded-xl px-4 py-2 text-sm font-semibold transition ${viewMode === "month" ? "calendar-view-toggle-button--active" : ""}`}
                  >
                    {copy.monthly}
                  </button>
                </div>

                <div className="calendar-range-nav flex items-center gap-2 rounded-2xl p-1">
                  <button type="button" className="calendar-range-nav-button rounded-xl px-3 py-2 text-sm font-semibold transition" onClick={() => shiftVisibleRange(-1)}>{copy.prev}</button>
                  <button type="button" className="calendar-range-nav-button rounded-xl px-3 py-2 text-sm font-semibold transition" onClick={() => shiftVisibleRange(1)}>{copy.next}</button>
                </div>

                <button type="button" className={secondaryButtonClassName} onClick={handleResetToToday}>{viewMode === "week" ? copy.thisWeek : copy.thisMonth}</button>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {canAccessAllTeams || currentUserTeams.length > 1 ? (
                  <select value={selectedTeamId} onChange={event => setSelectedTeamId(event.target.value)} className={`${inputClassName} sm:!w-[220px] lg:!w-[190px]`}>
                    {canAccessAllTeams ? <option value="all">{copy.allTeams}</option> : null}
                    {availableTeamOptions.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                ) : null}

                <select value={memberFilter} onChange={event => setMemberFilter(event.target.value)} className={`${inputClassName} sm:!w-[220px] lg:!w-[190px]`}>
                  <option value="all">{copy.allTeamMembers}</option>
                  {selectedTeamMembers.map(member => (
                    <option key={member.email} value={member.email}>{member.name}</option>
                  ))}
                </select>

                <select value={typeFilter} onChange={event => setTypeFilter(event.target.value as "all" | CalendarAssignmentType)} className={`${inputClassName} sm:!w-[220px] lg:!w-[190px]`}>
                  <option value="all">{copy.allAssignmentTypes}</option>
                  {assignmentTypes.map(option => (
                    <option key={option} value={option}>{getAssignmentTypeLabel(option)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="relative">
            {viewMode === "week" ? renderWeekCalendar() : renderMonthCalendar()}
          </div>
        </SectionCard>

        <div className="space-y-6">
          <SectionCard>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.coverageGaps}</p>
            <h2 className="mt-3 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{copy.unscheduledTeammates}</h2>
            <p className="mt-2 text-sm text-[#667085]">{copy.unscheduledTeammatesDescription(viewMode)}</p>

            <div className="relative mt-5">
              {isLoading ? (
                <div className="flex min-h-[96px] items-center justify-center rounded-[20px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-4 py-5">
                  <LoadingSpinnerBadge label={copy.loadingCoverageGaps} />
                </div>
              ) : (
                <div className="space-y-3">
                  {unscheduledMembers.length > 0 ? unscheduledMembers.map(member => (
                    <div key={member.email} className="flex items-center gap-3 rounded-[20px] border border-[#eaecf0] bg-[#fcfcfd] px-4 py-4">
                      <UserAvatarBadge avatarUrl={member.avatarUrl ?? null} initials={member.initials} className="brand-primary-avatar flex h-10 w-10 items-center justify-center rounded-full text-[11px] font-bold" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-[#101828]">{member.name}</p>
                        <p className="truncate text-xs text-[#667085]">{member.role}</p>
                      </div>
                      {canCreateAssignments ? <button
                        type="button"
                        onClick={() => {
                          const memberTeamId = selectedTeamId !== "all"
                            ? selectedTeamId
                            : member.teamIds.find(teamId => availableTeamOptions.some(team => team.id === teamId)) ?? defaultDraftTeamId;
                          const nextTeamMembers = memberTeamId ? activeMembers.filter(candidate => candidate.teamIds.includes(memberTeamId)) : [];
                          setDraft(currentDraft => ({
                            ...createEmptyDraft(visibleRangeStart, memberTeamId, member.email),
                            type: currentDraft.type,
                            memberEmail: nextTeamMembers.some(candidate => candidate.email === member.email) ? member.email : nextTeamMembers[0]?.email ?? "",
                          }));
                          setEditingAssignmentId(null);
                          setError(null);
                          setIsModalOpen(true);
                        }}
                        className="rounded-xl bg-[var(--brand-primary-soft)] px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] transition hover:bg-[var(--brand-primary-soft-strong)]"
                      >
                        {copy.assign}
                      </button> : null}
                    </div>
                  )) : (
                    <div className="rounded-[20px] border border-[#dcfce7] bg-[#f0fdf4] px-4 py-5 text-sm text-[#166534]">{copy.everyMemberCovered(viewMode)}</div>
                  )}
                </div>
              )}
            </div>
          </SectionCard>
        </div>
      </div>

      <AnimatedModal
        open={isModalOpen}
        onClose={handleCloseModal}
        backdropAriaLabel={copy.closeScheduleAssignment}
        dialogLabelledBy="calendar-assignment-modal-title"
        overlayClassName="fixed inset-0 z-[90] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-10 flex max-h-[calc(100vh-4rem)] w-full max-w-3xl flex-col rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.scheduleEditor}</p>
            <h2 id="calendar-assignment-modal-title" className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">{editingAssignmentId ? copy.updateAssignment : copy.createAssignment}</h2>
            <p className="mt-2 max-w-2xl text-sm text-[#667085]">{copy.scheduleEditorDescription}</p>
          </div>

          <button type="button" onClick={handleCloseModal} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]">×</button>
        </div>

        <div className="mt-6 space-y-5 overflow-y-auto pr-1 pb-2">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <label className="space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.assignmentTitle}</span>
              <input type="text" value={draft.title} onChange={event => setDraft(current => ({ ...current, title: event.target.value }))} className={inputClassName} placeholder={copy.assignmentTitlePlaceholder} />
            </label>

            <label className="space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.assignmentType}</span>
              <select value={draft.type} onChange={event => setDraft(current => ({ ...current, type: event.target.value as CalendarAssignmentType }))} className={inputClassName}>
                {assignmentTypes.map(option => (
                  <option key={option} value={option}>{getAssignmentTypeLabel(option)}</option>
                ))}
              </select>
            </label>

            <label className="space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.team}</span>
              <select
                value={draft.teamId}
                onChange={event => {
                  const nextTeamId = event.target.value;
                  const nextTeamMembers = activeMembers.filter(member => member.teamIds.includes(nextTeamId));
                  const nextMemberEmail = nextTeamMembers.find(member => member.email.toLowerCase() === currentUserEmail?.toLowerCase())?.email ?? nextTeamMembers[0]?.email ?? "";
                  setDraft(current => ({
                    ...current,
                    teamId: nextTeamId,
                    memberEmail: nextTeamMembers.some(member => member.email === current.memberEmail) ? current.memberEmail : nextMemberEmail,
                  }));
                }}
                className={inputClassName}
                disabled={availableTeamOptions.length <= 1 && selectedTeamId !== "all"}
              >
                {availableTeamOptions.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </label>

            <label className="space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.teamMember}</span>
              <select value={draft.memberEmail} onChange={event => setDraft(current => ({ ...current, memberEmail: event.target.value }))} className={inputClassName}>
                {draftTeamMembers.map(member => (
                  <option key={member.email} value={member.email}>{member.name}</option>
                ))}
              </select>
            </label>

            <label className="space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.start}</span>
              <input type="datetime-local" value={draft.startAt} onChange={event => setDraft(current => ({ ...current, startAt: event.target.value }))} className={inputClassName} />
            </label>

            <label className="space-y-2">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.end}</span>
              <input type="datetime-local" value={draft.endAt} onChange={event => setDraft(current => ({ ...current, endAt: event.target.value }))} className={inputClassName} />
            </label>
          </div>

          <label className="space-y-2 block">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.notes}</span>
            <textarea value={draft.notes} onChange={event => setDraft(current => ({ ...current, notes: event.target.value }))} rows={4} className={`${inputClassName} resize-none`} placeholder={copy.notesPlaceholder} />
          </label>

          {error ? <div className="rounded-[20px] border border-[#fda4af] bg-[#fff1f2] px-4 py-3 text-sm font-medium text-[#9f1239]">{error}</div> : null}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-[#eaecf0] pt-5">
          <div>
            {editingAssignmentId ? (
              <button type="button" onClick={() => void handleDelete()} disabled={busy} className="rounded-2xl border border-[#fecaca] bg-white px-4 py-2.5 text-sm font-semibold text-[#b42318] transition hover:bg-[#fff5f5] disabled:cursor-not-allowed disabled:opacity-60">{copy.deleteAssignment}</button>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={handleCloseModal} className={secondaryButtonClassName} disabled={busy}>{copy.cancel}</button>
            <button type="button" onClick={() => void handleSave()} className={primaryButtonClassName} disabled={busy}>{busy ? copy.saving : editingAssignmentId ? copy.saveChanges : copy.createAssignmentAction}</button>
          </div>
        </div>
      </AnimatedModal>

      <AnimatedModal
        open={isAssignmentTypeModalOpen}
        onClose={handleCloseAssignmentTypeModal}
        backdropAriaLabel={copy.closeAssignmentTypeDialog}
        dialogLabelledBy="calendar-assignment-type-modal-title"
        overlayClassName="fixed inset-0 z-[90] flex items-center justify-center bg-[#101828]/36 p-6 backdrop-blur-[4px]"
        dialogClassName="app-modal-dialog relative z-10 flex max-h-[calc(100vh-4rem)] w-full max-w-3xl flex-col rounded-[32px] border border-white/70 bg-[#fcfcfd] p-7 shadow-[0_32px_100px_rgba(15,23,42,0.24)]"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.calendarSettings}</p>
            <h2 id="calendar-assignment-type-modal-title" className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.05em] text-[#101828]">{copy.assignmentTypesTitle}</h2>
            <p className="mt-2 max-w-2xl text-sm text-[#667085]">{copy.assignmentTypesDescription}</p>
          </div>

          <button type="button" onClick={handleCloseAssignmentTypeModal} disabled={assignmentTypeBusy} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d] disabled:cursor-not-allowed disabled:opacity-60">×</button>
        </div>

        <div className="mt-6 space-y-5 overflow-y-auto pr-1 pb-2">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-[minmax(0,240px)_minmax(0,1fr)]">
            <label className="space-y-2 block">
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#667085]">{copy.team}</span>
              <select value={assignmentTypeTeamId} onChange={event => setAssignmentTypeTeamId(event.target.value)} className={inputClassName} disabled={manageableTeamOptions.length <= 1 || assignmentTypeBusy}>
                {manageableTeamOptions.map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </label>

            <div className="rounded-[24px] border border-[#eaecf0] bg-white p-5 shadow-[0_18px_44px_rgba(15,23,42,0.05)]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.newAssignmentType}</p>
              <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  value={newAssignmentTypeName}
                  onChange={event => setNewAssignmentTypeName(event.target.value)}
                  className={inputClassName}
                  placeholder={copy.newAssignmentTypePlaceholder}
                  disabled={!assignmentTypeTeamId || assignmentTypeBusy}
                />
                <button type="button" onClick={() => void handleCreateAssignmentType()} className={primaryButtonClassName} disabled={!assignmentTypeTeamId || assignmentTypeBusy}>
                  {assignmentTypeBusy ? copy.saving : copy.addType}
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-[24px] border border-[#eaecf0] bg-white p-5 shadow-[0_18px_44px_rgba(15,23,42,0.05)]">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.existingTypes}</p>
                <p className="mt-2 text-sm text-[#667085]">{copy.existingTypesDescription}</p>
              </div>
              <span className="rounded-full bg-[#f2f4f7] px-3 py-1 text-xs font-semibold text-[#475467]">{copy.typesCount(filteredAssignmentTypeDefinitions.length)}</span>
            </div>

            <div className="mt-4 space-y-3">
              {filteredAssignmentTypeDefinitions.length > 0 ? filteredAssignmentTypeDefinitions.map(assignmentType => {
                const isEditing = editingAssignmentTypeId === assignmentType.id;

                return (
                  <div key={assignmentType.id} className="rounded-[20px] border border-[#eaecf0] bg-[#fcfcfd] p-4">
                    {isEditing ? (
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                        <input
                          type="text"
                          value={editingAssignmentTypeName}
                          onChange={event => setEditingAssignmentTypeName(event.target.value)}
                          className={inputClassName}
                          disabled={assignmentTypeBusy}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          <button type="button" onClick={() => void handleSaveAssignmentTypeEdit(assignmentType)} className={primaryButtonClassName} disabled={assignmentTypeBusy}>
                            {assignmentTypeBusy ? copy.saving : copy.save}
                          </button>
                          <button type="button" onClick={handleCancelEditingAssignmentType} className={secondaryButtonClassName} disabled={assignmentTypeBusy}>{copy.cancel}</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-[#101828]">{assignmentType.name}</p>
                          <p className="mt-1 text-xs text-[#667085]">{copy.availableWhenScheduling}</p>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button type="button" onClick={() => handleStartEditingAssignmentType(assignmentType)} className={secondaryButtonClassName} disabled={assignmentTypeBusy}>{copy.edit}</button>
                          <button type="button" onClick={() => void handleDeleteAssignmentType(assignmentType)} className="rounded-2xl border border-[#fecaca] bg-white px-4 py-2.5 text-sm font-semibold text-[#b42318] transition hover:bg-[#fff5f5] disabled:cursor-not-allowed disabled:opacity-60" disabled={assignmentTypeBusy}>{copy.delete}</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              }) : (
                <div className="rounded-[20px] border border-dashed border-[#d0d5dd] bg-[#f8fafc] px-4 py-5 text-sm text-[#667085]">{copy.noAssignmentTypesYet}</div>
              )}
            </div>
          </div>

          {assignmentTypeError ? <div className="rounded-[20px] border border-[#fda4af] bg-[#fff1f2] px-4 py-3 text-sm font-medium text-[#9f1239]">{assignmentTypeError}</div> : null}
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-[#eaecf0] pt-5">
          <button type="button" onClick={handleCloseAssignmentTypeModal} className={secondaryButtonClassName} disabled={assignmentTypeBusy}>{copy.done}</button>
        </div>
      </AnimatedModal>
    </div>
  );
}
